// Compliance Item Card Component
// Displays compliance item details with deep links to asset and rule

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ComplianceItemCardProps {
  readonly assetId?: string;
  readonly assetName: string;
  readonly assetCode?: string;
  readonly regimeCode: string;
  readonly regimeName: string;
  readonly complianceCheckId?: number;
  readonly onNavigateToAsset?: (assetId: string) => void;
  readonly onNavigateToRule?: (ruleId: number) => void;
}

export function ComplianceItemCard({
  assetId,
  assetName,
  assetCode,
  regimeCode,
  regimeName,
  complianceCheckId,
  onNavigateToAsset,
  onNavigateToRule,
}: Readonly<ComplianceItemCardProps>) {
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [showRuleDialog, setShowRuleDialog] = useState(false);

  const handleAssetClick = () => {
    if (assetId && onNavigateToAsset) {
      setShowAssetDialog(true);
    }
  };

  const handleRuleClick = () => {
    if (complianceCheckId && onNavigateToRule) {
      setShowRuleDialog(true);
    }
  };

  const confirmAssetNavigation = () => {
    if (assetId && onNavigateToAsset) {
      onNavigateToAsset(assetId);
      setShowAssetDialog(false);
    }
  };

  const confirmRuleNavigation = () => {
    if (complianceCheckId && onNavigateToRule) {
      onNavigateToRule(complianceCheckId);
      setShowRuleDialog(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <>
      <div className="bg-white dark:bg-black border rounded-lg p-5 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base font-semibold">Compliance Item</h3>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Asset */}
          <div className="space-y-2">
            <Label className="text-muted-foreground font-semibold text-xs uppercase tracking-wide">
              Asset
            </Label>
            <div className="space-y-1">
              {assetId && onNavigateToAsset ? (
                <button
                  type="button"
                  onClick={handleAssetClick}
                  className="text-left hover:underline focus:outline-none focus:underline"
                  title={assetName}
                >
                  <p className="text-sm font-bold text-primary">{truncateText(assetName)}</p>
                  {assetCode && (
                    <p className="text-xs text-primary mt-0.5">{assetCode}</p>
                  )}
                </button>
              ) : (
                <div>
                  <p className="text-sm font-bold text-primary">{truncateText(assetName)}</p>
                  {assetCode && (
                    <p className="text-xs text-primary mt-0.5">{assetCode}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Rule Code */}
          <div className="space-y-2">
            <Label className="text-muted-foreground font-semibold text-xs uppercase tracking-wide">
              Regulation
            </Label>
            <div className="space-y-1">
              {complianceCheckId && onNavigateToRule ? (
                <button
                  type="button"
                  onClick={handleRuleClick}
                  className="text-left hover:underline focus:outline-none focus:underline"
                  title={regimeName}
                >
                  <p className="text-sm font-bold text-primary">{regimeCode}</p>
                  <p className="text-xs text-primary mt-0.5">{truncateText(regimeName, 40)}</p>
                </button>
              ) : (
                <div>
                  <p className="text-sm font-bold text-primary">{regimeCode}</p>
                  <p className="text-xs text-primary mt-0.5">{truncateText(regimeName, 40)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Asset Navigation Confirmation Dialog */}
      <AlertDialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Navigate to Asset Details?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be taken to the asset details page for <strong>{assetName}</strong>. 
              Any unsaved changes on this form will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAssetNavigation}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rule Navigation Confirmation Dialog */}
      <AlertDialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Navigate to Regulation Details?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be taken to the regulation details page for <strong>{regimeCode}</strong>. 
              Any unsaved changes on this form will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRuleNavigation}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
